import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, FolderTree, Copy, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/ImageUpload";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

// Mirror of the backend `_slugify` — kept simple so admins get a live preview
// of what URL the category will be reachable at while typing.
const slugify = (s) =>
  (s || "").toString().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "category";

const EMPTY = {
  name: "", slug: "", display_order: 0, enabled: true, show_heading: true,
  banner_image_url: "", tagline: "", description: "", auto_add_to_basket: false,
};

/**
 * Standalone Product Categories management screen — mounted under
 * Admin → Categories so the ops team can curate the storefront taxonomy
 * without stepping through the full Products tab.
 *
 * Also surfaces the shareable `?category=<slug>` URL for each category so
 * marketing can link straight to a filtered shop grid.
 */
export default function CategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [catsRes, prodsRes] = await Promise.all([
        axios.get(`${API}/admin/product-categories`, { withCredentials: true }),
        axios.get(`${API}/admin/products`, { withCredentials: true }).catch(() => ({ data: { products: [] } })),
      ]);
      const cats = (catsRes.data.categories || []).slice().sort((a, b) => {
        const ao = Number(a.display_order ?? 0);
        const bo = Number(b.display_order ?? 0);
        if (ao !== bo) return ao - bo;
        return (a.name || "").localeCompare(b.name || "");
      });
      const prods = prodsRes.data.products || [];
      const map = {};
      for (const p of prods) {
        const k = p.category_id || "";
        map[k] = (map[k] || 0) + 1;
      }
      setCategories(cats);
      setCounts(map);
    } catch (e) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY });
    setShowDialog(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      slug: c.slug || "",
      display_order: c.display_order ?? 0,
      enabled: c.enabled !== false,
      show_heading: c.show_heading !== false,
      banner_image_url: c.banner_image_url || "",
      tagline: c.tagline || "",
      description: c.description || "",
      auto_add_to_basket: !!c.auto_add_to_basket,
    });
    setShowDialog(true);
  };
  const save = async () => {
    const name = (form.name || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name,
        slug: (form.slug || "").trim(),
        display_order: Number(form.display_order || 0),
        enabled: form.enabled !== false,
        show_heading: form.show_heading !== false,
        banner_image_url: (form.banner_image_url || "").trim(),
        tagline: (form.tagline || "").trim(),
        description: (form.description || "").trim(),
        auto_add_to_basket: !!form.auto_add_to_basket,
      };
      if (editing) {
        await axios.put(`${API}/admin/product-categories/${editing.category_id}`, body, { withCredentials: true });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/product-categories`, body, { withCredentials: true });
        toast.success("Category created");
      }
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Products in this category will become uncategorised.`)) return;
    try {
      await axios.delete(`${API}/admin/product-categories/${c.category_id}`, { withCredentials: true });
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  // Inline enable/disable toggle in the categories table. Sends the full
  // category body (backend PUT expects `ProductCategoryWrite`, not a
  // partial patch) with just `enabled` flipped, and optimistically updates
  // the local list so the switch feels instant. Any backend failure
  // rolls back the UI change and shows a toast.
  const toggleEnabled = async (c, nextEnabled) => {
    const prev = c.enabled !== false;
    setCategories((list) => list.map((x) =>
      x.category_id === c.category_id ? { ...x, enabled: nextEnabled } : x,
    ));
    try {
      await axios.put(
        `${API}/admin/product-categories/${c.category_id}`,
        {
          name: c.name || "",
          slug: c.slug || "",
          display_order: Number(c.display_order || 0),
          enabled: nextEnabled,
          show_heading: c.show_heading !== false,
          banner_image_url: c.banner_image_url || "",
          tagline: c.tagline || "",
          description: c.description || "",
        },
        { withCredentials: true },
      );
      toast.success(nextEnabled ? `"${c.name}" is now visible` : `"${c.name}" is now hidden site-wide`);
    } catch (e) {
      setCategories((list) => list.map((x) =>
        x.category_id === c.category_id ? { ...x, enabled: prev } : x,
      ));
      toast.error(e?.response?.data?.detail || "Failed to update visibility");
    }
  };
  // Inline switch for just the heading/label text — independent of
  // `enabled` above. Products still show, grouped the same way; only the
  // "Marketing"-style section heading above them is hidden when off.
  const toggleShowHeading = async (c, nextShowHeading) => {
    const prev = c.show_heading !== false;
    setCategories((list) => list.map((x) =>
      x.category_id === c.category_id ? { ...x, show_heading: nextShowHeading } : x,
    ));
    try {
      await axios.put(
        `${API}/admin/product-categories/${c.category_id}`,
        {
          name: c.name || "",
          slug: c.slug || "",
          display_order: Number(c.display_order || 0),
          enabled: c.enabled !== false,
          show_heading: nextShowHeading,
          banner_image_url: c.banner_image_url || "",
          tagline: c.tagline || "",
          description: c.description || "",
        },
        { withCredentials: true },
      );
      toast.success(nextShowHeading ? `"${c.name}" heading is now shown` : `"${c.name}" heading is now hidden`);
    } catch (e) {
      setCategories((list) => list.map((x) =>
        x.category_id === c.category_id ? { ...x, show_heading: prev } : x,
      ));
      toast.error(e?.response?.data?.detail || "Failed to update heading visibility");
    }
  };
  const copyUrl = (slug) => {
    const url = `${window.location.origin}/shop?category=${slug}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("URL copied"),
      () => toast.error("Copy failed"),
    );
  };

  // Live-preview slug the way the backend will save it, so admins never see
  // "Property Listing" saved silently as "property-listing" without warning.
  const previewSlug = form.slug ? slugify(form.slug) : slugify(form.name);

  return (
    <div className="space-y-6" data-testid="categories-admin">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold mb-1">Product Categories</h3>
          <p className="text-sm text-muted-foreground">
            Group products into sections on the shop grid. Each category
            gets a shareable URL like <code>?category=&lt;slug&gt;</code> that
            filters the public store to that section only.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="categories-admin-add">
          <Plus className="h-4 w-4 mr-1" />Add Category
        </Button>
      </div>

      <div className="border border-border rounded overflow-x-auto bg-background">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No categories yet — click <strong>Add Category</strong> to create one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Slug / URL</th>
                <th className="p-3 w-24">Order</th>
                <th className="p-3 w-24">Products</th>
                <th className="p-3 w-28">Visible</th>
                <th className="p-3 w-28">Heading</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => {
                const shopUrl = `/shop?category=${c.slug}`;
                return (
                  <tr key={c.category_id} className="border-t border-border" data-testid={`categories-admin-row-${c.category_id}`}>
                    <td className="p-3 font-medium">
                      <div className="flex items-center gap-2">
                        <FolderTree className="h-3.5 w-3.5 text-muted-foreground" />
                        {c.name}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted/50 px-1.5 py-0.5 rounded">{c.slug || "—"}</code>
                        {c.slug && (
                          <>
                            <button
                              type="button"
                              onClick={() => copyUrl(c.slug)}
                              className="text-muted-foreground hover:text-foreground"
                              title="Copy shareable URL"
                              data-testid={`categories-admin-copy-${c.category_id}`}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                            <a
                              href={shopUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                              title="Open in shop"
                              data-testid={`categories-admin-open-${c.category_id}`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-3">{c.display_order}</td>
                    <td className="p-3 text-muted-foreground">{counts[c.category_id] || 0}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={c.enabled !== false}
                          onCheckedChange={(val) => toggleEnabled(c, !!val)}
                          data-testid={`categories-admin-toggle-${c.category_id}`}
                          aria-label={c.enabled !== false ? "Hide category" : "Show category"}
                        />
                        <span className={`text-xs ${c.enabled !== false ? "text-emerald-600" : "text-muted-foreground"}`}>
                          {c.enabled !== false ? "On" : "Off"}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={c.show_heading !== false}
                          onCheckedChange={(val) => toggleShowHeading(c, !!val)}
                          data-testid={`categories-admin-heading-toggle-${c.category_id}`}
                          aria-label={c.show_heading !== false ? "Hide heading text" : "Show heading text"}
                        />
                        <span className={`text-xs ${c.show_heading !== false ? "text-emerald-600" : "text-muted-foreground"}`}>
                          {c.show_heading !== false ? "On" : "Off"}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`categories-admin-edit-${c.category_id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-red-600 hover:bg-red-50" data-testid={`categories-admin-delete-${c.category_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="categories-admin-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription>
              Lower display-order values appear first. The slug is auto-generated
              from the name — override it only when you need a specific URL.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="categories-admin-form-name"
              />
            </div>
            <div>
              <Label>Slug (URL)</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder={slugify(form.name) || "auto-generated"}
                data-testid="categories-admin-form-slug"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Will be saved as{" "}
                <code className="bg-muted/60 px-1 rounded">?category={previewSlug}</code>
              </p>
            </div>
            <div>
              <Label>Display order</Label>
              <Input
                type="number"
                step="1"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                data-testid="categories-admin-form-order"
              />
            </div>
            <div className="flex items-center justify-between rounded border border-border p-3 bg-muted/20">
              <div className="pr-3">
                <div className="text-sm font-medium">Visible on website</div>
                <p className="text-xs text-muted-foreground">
                  When off, this category and every product inside it are hidden
                  across the site (timeline, product carousels, filtered links).
                  Admin CRUD is unaffected.
                </p>
              </div>
              <Switch
                checked={form.enabled !== false}
                onCheckedChange={(val) => setForm({ ...form, enabled: !!val })}
                data-testid="categories-admin-form-enabled"
                aria-label="Toggle category visibility"
              />
            </div>
            <div className="flex items-center justify-between rounded border border-border p-3 bg-muted/20">
              <div className="pr-3">
                <div className="text-sm font-medium">Show heading on the shop grid</div>
                <p className="text-xs text-muted-foreground">
                  When off, this category's products still display — just without
                  the "{form.name || "Category"}" heading text above them.
                </p>
              </div>
              <Switch
                checked={form.show_heading !== false}
                onCheckedChange={(val) => setForm({ ...form, show_heading: !!val })}
                data-testid="categories-admin-form-show-heading"
                aria-label="Toggle heading visibility"
              />
            </div>
            <div className="flex items-center justify-between rounded border border-border p-3 bg-muted/20">
              <div className="pr-3">
                <div className="text-sm font-medium">Auto-add to basket on page load</div>
                <p className="text-xs text-muted-foreground">
                  When on, every free product in this category drops straight
                  into a shopper's basket the moment they open the shop — off
                  by default so nothing is added automatically.
                </p>
              </div>
              <Switch
                checked={!!form.auto_add_to_basket}
                onCheckedChange={(val) => setForm({ ...form, auto_add_to_basket: !!val })}
                data-testid="categories-admin-form-auto-add"
                aria-label="Toggle auto-add to basket"
              />
            </div>

            {/* --- Landing banner (optional) --- */}
            <div className="pt-3 border-t border-border space-y-3">
              <div>
                <div className="text-sm font-medium">Landing banner (optional)</div>
                <p className="text-xs text-muted-foreground">
                  Rendered above the product list when someone opens{" "}
                  <code className="bg-muted/60 px-1 rounded">?category={previewSlug}</code>.
                  Leave everything blank to keep the plain list.
                </p>
              </div>
              <div>
                <Label>Banner image</Label>
                <ImageUpload
                  onUpload={(url) => {
                    const u = Array.isArray(url) ? url[0] : url;
                    setForm((f) => ({ ...f, banner_image_url: u || "" }));
                  }}
                  multiple={false}
                  label="Upload banner image"
                  skipWatermark={true}
                />
                {form.banner_image_url && (
                  <div className="mt-2 flex items-center gap-2 p-2 border border-border bg-muted/20">
                    <img
                      src={form.banner_image_url}
                      alt="Banner preview"
                      className="h-12 w-24 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, banner_image_url: "" }))}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="categories-admin-form-banner-clear"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <Input
                  className="mt-2"
                  placeholder="Or paste image URL"
                  value={form.banner_image_url || ""}
                  onChange={(e) => setForm({ ...form, banner_image_url: e.target.value })}
                  data-testid="categories-admin-form-banner-url"
                />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Short one-liner shown under the name"
                  maxLength={140}
                  data-testid="categories-admin-form-tagline"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="A longer marketing blurb rendered under the tagline"
                  rows={3}
                  data-testid="categories-admin-form-description"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="categories-admin-save">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : (editing ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
